import React from 'react';
import { Record } from '../../types';
import { useAuth } from '../../context/AuthContext';

interface RecordsTableProps {
  records: Record[];
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onShowChangeHistory: (record: Record, fieldLabel: string) => void;
}

// Induction-date highlight: presentation-only color coding keyed by how the
// induction year compares to the current year (offset = inductionYear - currentYear).
const INDUCTION_YEAR_COLORS: { [offset: number]: string } = {
  0: '#ff5252', // current year — red
  1: '#ffa726', // current year + 1 — orange
  2: '#66bb6a', // current year + 2 — green
  3: '#42a5f5', // current year + 3 — blue
};

/** Parse a 4-digit year from an ISO (YYYY-MM-DD) or otherwise parseable date string. */
function parseYear(dateStr: string): number | null {
  const iso = dateStr.match(/^(\d{4})-\d{2}-\d{2}/);
  if (iso) return parseInt(iso[1], 10);
  const parsed = new Date(dateStr);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getFullYear();
}

/** Bold + colored style for induction dates in the current year through +3; undefined otherwise. */
function inductionYearStyle(dateStr: string | undefined): React.CSSProperties | undefined {
  if (!dateStr) return undefined;
  const year = parseYear(dateStr);
  if (year === null) return undefined;
  const color = INDUCTION_YEAR_COLORS[year - new Date().getFullYear()];
  return color ? { fontWeight: 'bold', color } : undefined;
}

const checkBadgeClass: { [key: string]: string } = {
  'A-Check': 'badge-serviceable',
  'B-Check': 'badge-overhauled',
  'C-Check': 'badge-overhaul-due',
  'D-Check': 'badge-unserviceable',
};

const statusBadgeClass: { [key: string]: string } = {
  Complete: 'badge-complete',
  'In Work': 'badge-in-work',
  'In Transit': 'badge-in-transit',
  'In Planning': 'badge-in-planning',
  Scheduled: 'badge-scheduled',
  'On Hold': 'badge-on-hold',
};

const ChangeIndicator: React.FC<{ record: Record; fieldLabel: string; onClick: () => void }> = ({
  record,
  fieldLabel,
  onClick,
}) => {
  const hasChange = record.date_change_history?.some((h) => h.label === fieldLabel);
  if (!hasChange) return null;
  return (
    <button className="change-indicator" title="Date changed" onClick={onClick}>
      !
    </button>
  );
};

/** Renders a single record row with action buttons. */
const RecordRow: React.FC<{
  record: Record;
  canEdit: boolean;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onShowChangeHistory: (record: Record, fieldLabel: string) => void;
}> = ({ record: r, canEdit, onView, onEdit, onDelete, onShowChangeHistory }) => (
  <tr key={r.id}>
    <td>{r.carrier}</td>
    <td>
      <strong>{r.tail}</strong>
    </td>
    <td>{r.aircraft_type}</td>
    <td>{r.ldg_induction_date || '\u2014'}</td>
    <td>
      {r.delivery_date || '\u2014'}
      <ChangeIndicator
        record={r}
        fieldLabel="Delivery Date"
        onClick={() => onShowChangeHistory(r, 'Delivery Date')}
      />
    </td>
    <td>
      <span style={inductionYearStyle(r.induction_date)}>{r.induction_date || '\u2014'}</span>
      <ChangeIndicator
        record={r}
        fieldLabel="Induction Date"
        onClick={() => onShowChangeHistory(r, 'Induction Date')}
      />
    </td>
    <td>
      <span className={`badge ${checkBadgeClass[r.check_type] || ''}`}>
        {r.check_type}
      </span>
    </td>
    <td>
      <span className={`badge ${statusBadgeClass[r.status] || ''}`}>
        {r.status}
      </span>
    </td>
    <td>
      <button className="btn-icon view" title="View" onClick={() => onView(r.id)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
      {canEdit && (
        <>
          <button className="btn-icon edit" title="Edit" onClick={() => onEdit(r.id)}>
            &#9998;
          </button>
          <button className="btn-icon delete" title="Delete" onClick={() => onDelete(r.id)}>
            &#128465;
          </button>
        </>
      )}
    </td>
  </tr>
);

const RecordsTable: React.FC<RecordsTableProps> = ({ records, onView, onEdit, onDelete, onShowChangeHistory }) => {
  const { canEdit } = useAuth();

  if (!records.length) {
    return (
      <section className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Carrier</th>
              <th>Tail #</th>
              <th>Aircraft Type</th>
              <th>LDG Induction Date</th>
              <th>LDG Delivery Date</th>
              <th>A/C Induction Date</th>
              <th>Check Type</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-dim)' }}>
                No records found
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    );
  }

  return (
    <section className="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Carrier</th>
            <th>Tail #</th>
            <th>Aircraft Type</th>
            <th>LDG Induction Date</th>
            <th>LDG Delivery Date</th>
            <th>A/C Induction Date</th>
            <th>Check Type</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r) => (
            <RecordRow
              key={r.id}
              record={r}
              canEdit={canEdit}
              onView={onView}
              onEdit={onEdit}
              onDelete={onDelete}
              onShowChangeHistory={onShowChangeHistory}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
};

export default RecordsTable;
